import { CHANNELS, type Channel } from '@beta-assur/shared';
import { useState } from 'react';
import { ConfirmButton } from '../components/confirm';
import { Field } from '../components/page';
import { ActionError } from '../components/states';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input } from '../components/ui/input';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import type { Policyholder } from '../lib/types';

// Opposition par canal, vérification du numéro, export et anonymisation (BA-042, BA-043).

/** Téléchargement d'une réponse de l'API (PDF, JSON) sous un nom de fichier. */
export async function download(
  api: ReturnType<typeof useAuth>['api'],
  path: string,
  filename: string,
) {
  const res = await api.request<Response>('GET', path, { raw: true });
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function PolicyholderRightsCard({
  holder,
  onChange,
}: {
  holder: Policyholder;
  onChange: () => void;
}) {
  const t = useI18n().t;
  const auth = useAuth();
  const [channels, setChannels] = useState<Channel[]>(holder.optOutChannels);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<Error | null>(null);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      onChange();
    } catch (err) {
      setError(err as Error);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.rights.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">{t.rights.optOutChannels}</legend>
          <div className="flex flex-wrap gap-4">
            {CHANNELS.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={channels.includes(c)}
                  onChange={(e) =>
                    setChannels((list) =>
                      e.target.checked ? [...list, c] : list.filter((x) => x !== c),
                    )
                  }
                />
                {t.channels[c]}
              </label>
            ))}
          </div>
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void run(() =>
                  auth.api.request('PUT', `/v1/policyholders/${holder.id}/opt-out`, {
                    body: { channels },
                  }),
                )
              }
            >
              {t.rights.saveOptOut}
            </Button>
          </div>
        </fieldset>

        {holder.phone ? (
          <div className="flex flex-col gap-2">
            <p className="flex items-center gap-2 text-sm font-medium">
              {t.rights.verifyPhone}
              {holder.phoneVerifiedAt ? <Badge tone="success">{t.rights.verified}</Badge> : null}
            </p>
            {!codeSent ? (
              <div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    void run(async () => {
                      await auth.api.request(
                        'POST',
                        `/v1/policyholders/${holder.id}/phone-verification`,
                      );
                      setCodeSent(true);
                    })
                  }
                >
                  {t.rights.sendCode}
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-2">
                <p className="w-full text-xs text-muted-foreground">{t.rights.codeSent}</p>
                <Field label={t.rights.code}>
                  <Input
                    inputMode="numeric"
                    maxLength={8}
                    className="w-32"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Field>
                <Button
                  size="sm"
                  disabled={code.length < 4}
                  onClick={() =>
                    void run(async () => {
                      await auth.api.request(
                        'POST',
                        `/v1/policyholders/${holder.id}/phone-verification/confirm`,
                        { body: { code } },
                      );
                      setCodeSent(false);
                      setCode('');
                    })
                  }
                >
                  {t.rights.confirmCode}
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {auth.can('administer') ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void run(() =>
                  download(
                    auth.api,
                    `/v1/policyholders/${holder.id}/export`,
                    `assure-${holder.id}.json`,
                  ),
                )
              }
            >
              {t.rights.export}
            </Button>
            <ConfirmButton
              label={t.rights.anonymize}
              title={t.rights.anonymize}
              message={t.rights.anonymizeConfirm}
              expected="ANONYMISER"
              variant="destructive"
              onConfirm={async () => {
                await auth.api.request('POST', `/v1/policyholders/${holder.id}/anonymize`, {
                  body: { confirm: 'ANONYMISER' },
                });
                onChange();
              }}
            />
          </div>
        ) : null}
        <ActionError error={error} />
      </CardContent>
    </Card>
  );
}
